import React, { useState } from 'react';
import useSoftphone from './useSoftphone';
import './softphone.css';

const SoftphoneUI = () => {
  const {
    sipUrl, setSipUrl,
    sipUser, setSipUser,
    sipPass, setSipPass,
    agentName, setAgentName,
    target, setTarget,
    status,
    registered,
    incoming,
    callState,
    agentState,
    callerInfo,
    outputDevices,
    selectedOutput, setSelectedOutput,
    muted, setMuted,
    onHold, setOnHold,
    micVolume, setMicVolume,
    speakerVolume, setSpeakerVolume,
    networkStats,
    callHistory,
    register,
    call,
    answer,
    hangup,
    startAttendedTransfer,
    completeAttendedTransfer,
    cancelAttendedTransfer,
    blindTransfer,
    mute,
    unmute,
    hold,
    unhold,
    emitStatus
  } = useSoftphone();

  const qualityColor = () => {
    const { rtt, jitter, loss } = networkStats;
    if (rtt > 300 || jitter > 60 || loss >= 3) return 'red';
    if (rtt > 150 || jitter > 30 || loss >= 1) return 'orange';
    return 'green';
  };

  return (
    <div className="softphone-wrapper">
      <h2 className="title">📞 HotLync Softphone</h2>

      <div className="status-bar">
        <span>Status: <strong>{status}</strong></span>
        <span>Agent: <strong>{agentName}</strong></span>
        <span className={`agent-state ${agentState}`}>{agentState.toUpperCase()}</span>
      </div>

      <div className="login-form">
        <input placeholder="SIP URL" value={sipUrl} onChange={e => setSipUrl(e.target.value)} />
        <input placeholder="Username" value={sipUser} onChange={e => setSipUser(e.target.value)} />
        <input type="password" placeholder="Password" value={sipPass} onChange={e => setSipPass(e.target.value)} />
        <input placeholder="Agent Name" value={agentName} onChange={e => setAgentName(e.target.value)} />
        {!registered ? (
          <button onClick={register} className="register-btn">✅ Register</button>
        ) : (
          <button onClick={() => { emitStatus('logged_out'); window.location.reload(); }} className="register-btn red">🚫 Unregister</button>
        )}
      </div>

      {registered && (
        <div className="agent-controls">
          <label>Status:
            <select value={agentState} onChange={e => {
              const next = e.target.value;
              if (next !== 'busy') emitStatus(next);
            }}>
              <option value="ready">Ready</option>
              <option value="on_break">On Break</option>
              <option value="logged_out">Log Out</option>
            </select>
          </label>
        </div>
      )}

      <div className="dialer">
        <input placeholder="Target Extension" value={target} onChange={e => setTarget(e.target.value)} />
        {callState === 'idle' && <button className="call-btn" onClick={() => call()}>📞 Call</button>}

        {callState === 'in_call' && (
          <div className="call-actions">
            <button className="button transfer" onClick={startAttendedTransfer} disabled={!target}>🔁 Attended</button>
            <button className="button blind" onClick={blindTransfer} disabled={!target}>🎯 Blind</button>

            <button
              className={`button ${muted ? 'unmute' : 'mute'}`}
              onClick={() => {
                if (muted) {
                  unmute();
                  setMuted(false);
                } else {
                  mute();
                  setMuted(true);
                }
              }}
            >
              {muted ? '🔊 Unmute' : '🔇 Mute'}
            </button>

            <button
              className={`button ${onHold ? 'unhold' : 'hold'}`}
              onClick={() => {
                if (onHold) {
                  unhold();
                  setOnHold(false);
                } else {
                  hold();
                  setOnHold(true);
                }
              }}
            >
              {onHold ? '▶️ Unhold' : '⏸️ Hold'}
            </button>

            <button className="button danger" onClick={hangup}>🔚 Hangup</button>
          </div>
        )}

        {callState === 'transferring' && (
          <div className="transfer-actions">
            <button className="button success" onClick={completeAttendedTransfer}>✅ Complete</button>
            <button className="button warning" onClick={cancelAttendedTransfer}>❌ Cancel</button>
          </div>
        )}
      </div>

      {incoming && (
        <div className="incoming-call">
          <strong>📲 Incoming call:</strong> {callerInfo}<br />
          <button className="button success" onClick={answer}>✔️ Answer</button>
          <button className="button danger" onClick={hangup}>❌ Reject</button>
        </div>
      )}

      <div className="audio-settings">
        <label>Output:
          <select value={selectedOutput} onChange={e => setSelectedOutput(e.target.value)}>
            {outputDevices.map(dev => (
              <option key={dev.deviceId} value={dev.deviceId}>{dev.label || `Device ${dev.deviceId}`}</option>
            ))}
          </select>
        </label>

        <label>Mic Volume:
          <input type="range" min="0" max="1" step="0.1" value={micVolume} onChange={e => setMicVolume(Number(e.target.value))} />
        </label>
        <label>Speaker Volume:
          <input type="range" min="0" max="1" step="0.1" value={speakerVolume} onChange={e => setSpeakerVolume(Number(e.target.value))} />
        </label>
      </div>

      <div className="network-quality" style={{ color: qualityColor() }}>
        <strong>Network:</strong> RTT {networkStats.rtt}ms | Jitter {networkStats.jitter}ms | Loss {networkStats.loss}%
      </div>

      <div className="call-history">
        <h4>📜 Call History</h4>
        {callHistory.length === 0 ? (
          <p>No calls yet.</p>
        ) : (
          <ul>
            {callHistory.map((entry, i) => (
              <li key={i}>
                {entry.direction === 'incoming' ? '⬅️ From' : '➡️ To'} {entry.number} | {entry.timestamp} | {entry.duration ? `${entry.duration}s` : '—'}
                <button className="redial-btn" onClick={() => call(entry.number)}>🔁 Dial</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default SoftphoneUI;
